import { useMemo, useState } from "react";
import { Sun, Thermometer } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { IMAGES } from "../lib/images";

// CAM FİLMİ — heat slider: darkness controls light, heat particles, temp & rejection counters.
export function WindowTint() {
  const { t } = useLanguage();
  const [val, setVal] = useState(20); // 0..70

  const p = val / 70;
  const temp = Math.round(45 - p * (45 - 24));
  const rejection = Math.round(p * 99);
  const overlayOpacity = p * 0.82;

  const particles = useMemo(
    () =>
      Array.from({ length: 22 }).map(() => ({
        left: Math.random() * 100,
        top: Math.random() * 55,
        size: 2 + Math.random() * 5,
        delay: Math.random() * 3,
      })),
    []
  );

  return (
    <section className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40" data-testid="tint-section">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.tint.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.tint.title} className="heading text-5xl text-white md:text-6xl" />
          <FadeUp delay={0.1}>
            <p className="mt-6 font-inter text-base text-hars-muted md:text-lg">{t.tint.text}</p>
          </FadeUp>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-[#0F1115] p-5">
              <div className="flex items-center gap-2 text-hars-muted">
                <Thermometer size={16} /> <span className="font-inter text-xs">{t.tint.temp}</span>
              </div>
              <div className="heading mt-2 text-4xl tabular-nums text-white">{temp}°C</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0F1115] p-5">
              <div className="flex items-center gap-2 text-hars-muted">
                <Sun size={16} /> <span className="font-inter text-xs">{t.tint.rejection}</span>
              </div>
              <div className="heading mt-2 text-4xl tabular-nums text-hars-orange">%{rejection}</div>
            </div>
          </div>

          <div className="mt-8">
            <label htmlFor="tint-slider" className="mb-2 flex justify-between font-inter text-xs text-hars-muted">
              <span>{t.tint.darkness}</span>
              <span className="text-white">%{val}</span>
            </label>
            <input
              id="tint-slider"
              type="range"
              min="0"
              max="70"
              value={val}
              onChange={(e) => setVal(Number(e.target.value))}
              data-cursor
              data-testid="tint-slider"
              aria-label={t.tint.darkness}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-hars-orange"
              style={{ accentColor: "#F0742A" }}
            />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/5">
            <img src={IMAGES.interiorView} alt="Araç içinden dışarı bakış" className="absolute inset-0 h-full w-full object-cover" />
            {/* Heat particles */}
            <div className="pointer-events-none absolute inset-0" style={{ opacity: 1 - p }}>
              {particles.map((pt, i) => (
                <span
                  key={i}
                  className="absolute rounded-full bg-hars-orangeLight blur-[1px]"
                  style={{
                    left: `${pt.left}%`,
                    top: `${pt.top}%`,
                    width: pt.size,
                    height: pt.size,
                    boxShadow: "0 0 8px rgba(255,148,85,0.8)",
                    animation: `pulse 2.5s ease-in-out ${pt.delay}s infinite`,
                  }}
                />
              ))}
            </div>
            {/* Tint darkening overlay */}
            <div
              className="pointer-events-none absolute inset-0 bg-[#04101f]"
              style={{ opacity: overlayOpacity, transition: "opacity 120ms linear" }}
              aria-hidden="true"
            />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-hars-orange/10 to-transparent" style={{ opacity: 1 - p }} />
          </div>
        </div>
      </div>
    </section>
  );
}
